import styled, { css } from 'styled-components';

import { rgba } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { FLEX_ALIGN } from '@savgroup-front-common/types';

const titleMixin = css<{ $color?: string }>`
  color: ${({ theme, $color }) => {
    return (
      $color ||
      theme.newUI.defaultColors.heading ||
      theme.newUI.defaultColors.text
    );
  }};
  font-weight: ${fontWeight('bold')};

  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;

  & *::first-letter {
    text-transform: capitalize;
  }

  svg {
    margin-right: ${spacing('sm')};
    color: ${({ theme, $color }) => $color || theme.colors.headingTextColor};
  }
`;

const sizeMixin = (size: string) => css`
  font-size: ${size};
  svg {
    width: ${size};
    height: ${size};
  }
`;

export const $H1 = styled.h1<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h1)};
`;

export const $H2 = styled.h2<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h2)};
`;

export const $H3 = styled.h3<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h3)};
`;

export const $H4 = styled.h4<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h4)};
  height: ${spacing('xl')};
  color: ${({ theme }) => theme.systemColors.defaultTextColor};
`;

export const $H5 = styled.h5<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h5)};
  color: ${({ theme }) => theme.systemColors.defaultTextColor};
`;

export const $H6 = styled.h6<{ $color?: string }>`
  ${titleMixin};
  ${({ theme }) => sizeMixin(theme.fonts.size.heading.h6)};
  color: ${({ theme }) => theme.systemColors.defaultTextColor};
`;

const transitionMixin = css`
  transition-duration: 300ms;
`;

const copyMixin = css`
  position: relative;
  border-radius: ${({ theme }) => theme.tw.radius.sm};

  background: ${rgba('#ddd', 0)};
  > svg {
    opacity: 0;
    margin-left: ${spacing('sm')};
    ${transitionMixin};
    transition-duration: 1000ms;
    min-width: ${spacing('md')};
    transform: scale(1);
  }

  &:hover {
    > svg {
      opacity: 1;
    }

    background: ${rgba('#ddd', 1)};
  }
  &:active {
    background: ${rgba('#ccc', 1)};
    user-select: none;
    > svg {
      transition-duration: 100ms;
      transform: scale(1.3);
    }
  }
`;

export const $HeadingContainer = styled.div<{
  $align: FLEX_ALIGN;
  $withoutMargin: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 0;
  justify-content: ${({ $align }) => $align};

  &:not(:last-child) {
    margin-bottom: ${({ $withoutMargin }) =>
      $withoutMargin ? null : spacing('lg')};
  }
`;
export const $CopyStyle = styled.span<{ $hasCopy: boolean }>`
  white-space: pre-line;
  word-break: break-word;
  display: flex;
  align-items: center;

  width: fit-content;
  ${transitionMixin};
  padding: ${spacing('xs')};
  transform: translateX(-3px);
  ${({ $hasCopy }) => ($hasCopy ? copyMixin : null)};
`;

export const $HeadingGroup = styled.div`
  margin-bottom: ${spacing('xxl')};

  & *:last-child {
    margin-bottom: 0;
  }
`;
